import type { Plan } from '@/services/api/api.types'

import { FREE_PLAN_PRICE } from './onboardingConstants'

/**
 * Gets the free plan from a list of plans
 */
export function getFreePlan(plans: Plan[]): Plan | undefined {
  return plans.find(plan => plan.priceDollars === FREE_PLAN_PRICE)
}

/**
 * Formats price for display
 */
export function formatPlanPrice(dollars: number): string {
  return dollars === FREE_PLAN_PRICE ? 'Free' : `$${Math.round(dollars)}`
}

/**
 * Formats max users for display
 */
export function formatMaxUsers(maxUsers: number): string {
  if (maxUsers === 0) {
    return 'Unlimited users'
  }
  return `Up to ${maxUsers.toLocaleString()} users`
}

/**
 * Formats a user range for display (e.g., "0-10 users", "11-50 users", "500+ users")
 */
export function formatUserRange(minUsers: number, maxUsers: number): string {
  if (maxUsers === 0) {
    return `${minUsers.toLocaleString()}+ users`
  }
  return `${minUsers.toLocaleString()}-${maxUsers.toLocaleString()} users`
}

/**
 * Calculates user ranges for a sorted list of plans.
 * Returns an array of { min, max } objects corresponding to each plan.
 */
export function calculateUserRanges(sortedPlans: Plan[]): Array<{ min: number; max: number }> {
  return sortedPlans.map((plan, index) => {
    const previousMax = index === 0 ? 0 : sortedPlans[index - 1].maxUsers
    const min = previousMax === 0 ? previousMax : previousMax + 1
    return { min, max: plan.maxUsers }
  })
}

/**
 * Calculates the savings percentage compared to a baseline price
 */
export function calculateSavingsPercent(price: number, baselinePrice: number): number {
  if (baselinePrice <= 0 || price <= 0 || price >= baselinePrice) {
    return 0
  }
  return Math.round(((baselinePrice - price) / baselinePrice) * 100)
}

/**
 * Calculates how many seats until the next tier
 */
export function calculateSeatsUntilNextTier(currentUsers: number, currentPlanMaxUsers: number): number | null {
  if (currentPlanMaxUsers === 0) {
    return null
  }
  return Math.max(0, currentPlanMaxUsers - currentUsers + 1)
}
